import {chatReducer, type ChatTurnCancelledAction, type ToolCallResult} from '@microsoft/agent-host-protocol'
import type {RelayLease} from '@github-ui/ahp-relay/relay-registry'
import type {
  DispatchReceipt,
  EstablishedAhpDirectTurnTelemetry,
  EstablishedAhpPendingMessage,
  EstablishedAhpSessionCommands,
  EstablishedAhpSessionIdentity,
  EstablishedAhpSessionInputController,
  EstablishedAhpSessionView,
  EstablishedAhpSessionViewSnapshot,
  InputCompletion,
  PreparedPrompt,
  StopOutcome,
  ToolConfirmationResponse,
} from './contracts'

function attachmentsFor(prompt: PreparedPrompt) {
  return prompt.attachments && prompt.attachments.length > 0 ? [...prompt.attachments] : undefined
}

const ACTION_TURN_CANCELLED = 'chat/turnCancelled' as ChatTurnCancelledAction['type']

/**
 * React-free command and view client for one identity-bound relay lease.
 *
 * The client intentionally does not own the lease lifecycle. Its containing
 * established-session runtime owns acquisition, reset, and disposal.
 */
export class AhpSessionClient {
  readonly view: EstablishedAhpSessionView
  readonly input: EstablishedAhpSessionInputController
  readonly commands: EstablishedAhpSessionCommands
  readonly identity: EstablishedAhpSessionIdentity
  private readonly lease: RelayLease
  private readonly directTurnTelemetry: EstablishedAhpDirectTurnTelemetry | undefined

  #clientSeq = 0
  #disposed = false
  #viewSnapshot!: EstablishedAhpSessionViewSnapshot
  #optimisticallyCancelledTurnId: string | undefined
  readonly #viewListeners = new Set<() => void>()
  readonly #disposeListeners = new Set<(error: Error) => void>()
  readonly #unsubscribeView: () => void
  readonly #unsubscribeActions: () => void

  constructor(
    identity: EstablishedAhpSessionIdentity,
    lease: RelayLease,
    directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry,
  ) {
    this.identity = identity
    this.lease = lease
    this.directTurnTelemetry = directTurnTelemetry
    this.#viewSnapshot = this.readViewSnapshot()
    this.#updateTelemetryReadiness()
    this.#unsubscribeView = this.lease.subscribe(() => {
      if (this.#disposed || !this.lease.isCurrent()) return
      this.#viewSnapshot = this.readViewSnapshot()
      this.#updateTelemetryReadiness()
      for (const listener of this.#viewListeners) listener()
    })
    this.#unsubscribeActions = directTurnTelemetry
      ? this.lease.subscribeActions((envelope, previousState, state) =>
          directTurnTelemetry.acceptedAction(envelope, previousState, state),
        )
      : () => {}
    this.view = {
      getSnapshot: () => this.#viewSnapshot,
      subscribe: listener => {
        this.#viewListeners.add(listener)
        return () => this.#viewListeners.delete(listener)
      },
      observeChat: (channel, observer) => this.lease.observeChat(channel, observer),
      subscribeTelemetry: observer => this.lease.subscribeTelemetry(observer),
    }

    this.input = {
      submit: (prompt, requestId) => this.submit(prompt, requestId),
      queue: prompt => this.queue(prompt),
      steer: (prompt, steeringId) => this.steer(prompt, steeringId),
      sendNow: message => this.sendNow(message),
      removeQueued: id => this.removeQueued(id),
    }

    this.commands = {
      cancel: turnId => this.cancel(turnId),
      respondToPrompt: response => this.respondToPrompt(response),
      confirmTool: confirmation => this.confirmTool(confirmation),
      completeTool: input => this.completeTool(input),
      renameSession: title => this.renameSession(title),
      setApproveAll: enabled => this.setApproveAll(enabled),
      setMode: mode => this.setMode(mode),
    }
  }

  private readViewSnapshot(): EstablishedAhpSessionViewSnapshot {
    const state = this.lease.getSnapshot()
    if (this.#optimisticallyCancelledTurnId && state?.activeTurn?.id !== this.#optimisticallyCancelledTurnId) {
      this.#optimisticallyCancelledTurnId = undefined
    }
    const optimisticState =
      state && this.#optimisticallyCancelledTurnId
        ? chatReducer(state, {
            type: ACTION_TURN_CANCELLED,
            turnId: this.#optimisticallyCancelledTurnId,
            duration: 0,
          })
        : state
    return {
      state: optimisticState,
      status: this.lease.getStatus(),
      reachable: this.lease.getReachable(),
      error: this.lease.getError(),
      modelFailures: this.lease.getModelFailures(),
      provisioning: this.lease.getProvisioning(),
      sessionState: this.lease.getSessionState(),
      passive: this.lease.getPassive(),
      passiveKnown: this.lease.getPassiveKnown(),
      steeringMessages: this.lease.getSteeringMessages(),
    }
  }

  #updateTelemetryReadiness(): void {
    this.directTurnTelemetry?.readinessChanged(
      this.lease.getStatus() === 'connected' &&
        this.lease.getReachable() &&
        this.lease.getPassiveKnown() &&
        !this.lease.getPassive(),
    )
  }

  markDisposed(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.directTurnTelemetry?.readinessChanged(false)
    const error = new Error('established AHP session client is disposed')
    for (const listener of this.#disposeListeners) listener(error)
    this.#disposeListeners.clear()
    this.#unsubscribeView()
    this.#unsubscribeActions()
    this.#viewListeners.clear()
  }

  async waitForAccepted(receipt: DispatchReceipt, timeoutMs = 30_000): Promise<void> {
    this.ensureActive()
    if (!receipt.turnId) return
    const isAccepted = () => {
      const state = this.lease.getSnapshot()
      return state?.activeTurn?.id === receipt.turnId || state?.turns?.some(turn => turn.id === receipt.turnId) === true
    }
    if (isAccepted()) return

    await new Promise<void>((resolve, reject) => {
      let settled = false
      let unsubscribe = () => {}
      const finish = (callback: () => void) => {
        if (settled) return
        settled = true
        clearTimeout(timer)
        unsubscribe()
        this.#disposeListeners.delete(onDisposed)
        callback()
      }
      const onDisposed = (error: Error) => finish(() => reject(error))
      const timer = setTimeout(
        () =>
          finish(() =>
            reject(new Error('The first AHP turn was not accepted before the acknowledgement window closed.')),
          ),
        timeoutMs,
      )
      this.#disposeListeners.add(onDisposed)
      unsubscribe = this.lease.subscribe(() => {
        if (isAccepted()) finish(resolve)
      })
      if (this.#disposed) onDisposed(new Error('established AHP session client is disposed'))
    })
  }

  private ensureActive(): void {
    if (this.#disposed) throw new Error('established AHP session client is disposed')
  }

  private nextReceipt(
    details: Omit<DispatchReceipt, 'requestId' | 'clientSeq'>,
    requestId: string = crypto.randomUUID(),
  ): DispatchReceipt {
    return {
      requestId,
      clientSeq: ++this.#clientSeq,
      ...details,
    }
  }

  private async ensureMutationLane(requestId?: string): Promise<void> {
    this.ensureActive()
    const reconnected = await this.lease.ensureLive(
      requestId ? () => this.directTurnTelemetry?.readinessRequired(requestId) : undefined,
    )
    if (reconnected) {
      await this.lease.whenConnected()
    }
    if (this.lease.getPassive()) throw new Error('relay connection is read-only')
    if (requestId) this.directTurnTelemetry?.readinessObserved(requestId)
  }

  private async submit(prompt: PreparedPrompt, requestId?: string): Promise<DispatchReceipt> {
    const submissionRequestId = requestId ?? crypto.randomUUID()
    this.directTurnTelemetry?.beginSubmission({
      requestId: submissionRequestId,
      turnKind: 'existing_chat',
      startPath: 'active',
      ...(prompt.model?.id ? {modelId: prompt.model.id} : {}),
    })
    try {
      await this.ensureMutationLane(submissionRequestId)
      this.directTurnTelemetry?.turnDispatching(submissionRequestId)
      const turnId =
        prompt.contributedInstructions === undefined
          ? this.lease.dispatchTurn(prompt.text, attachmentsFor(prompt), prompt.model, prompt.agent)
          : this.lease.dispatchTurn(
              prompt.text,
              attachmentsFor(prompt),
              prompt.model,
              prompt.agent,
              prompt.contributedInstructions,
            )
      this.directTurnTelemetry?.turnDispatched(submissionRequestId, turnId)
      return this.nextReceipt({turnId}, submissionRequestId)
    } catch (error) {
      this.directTurnTelemetry?.preDispatchError(submissionRequestId)
      throw error
    }
  }

  private async queue(prompt: PreparedPrompt): Promise<DispatchReceipt> {
    await this.ensureMutationLane()
    const pendingMessageId =
      prompt.contributedInstructions === undefined
        ? this.lease.queueMessage(prompt.text, attachmentsFor(prompt), prompt.model, prompt.agent)
        : this.lease.queueMessage(
            prompt.text,
            attachmentsFor(prompt),
            prompt.model,
            prompt.agent,
            prompt.contributedInstructions,
          )
    return Promise.resolve(this.nextReceipt({pendingMessageId}))
  }

  private async steer(prompt: PreparedPrompt, steeringId?: string): Promise<DispatchReceipt> {
    await this.ensureMutationLane()
    const pendingMessageId =
      prompt.contributedInstructions === undefined
        ? this.lease.steerMessage(prompt.text, attachmentsFor(prompt), prompt.model, steeringId, prompt.agent)
        : this.lease.steerMessage(
            prompt.text,
            attachmentsFor(prompt),
            prompt.model,
            steeringId,
            prompt.agent,
            prompt.contributedInstructions,
          )
    return Promise.resolve(this.nextReceipt({pendingMessageId}))
  }

  private async sendNow(message: EstablishedAhpPendingMessage): Promise<DispatchReceipt> {
    const receipt = await this.steer(message.prompt, message.id)
    await this.removeQueued(message.id)
    return receipt
  }

  private async removeQueued(id: string): Promise<void> {
    await this.ensureMutationLane()
    this.lease.removeQueuedMessage(id)
    return Promise.resolve()
  }

  private cancel(turnId?: string): StopOutcome {
    this.ensureActive()
    const activeTurnId = turnId ?? this.lease.getSnapshot()?.activeTurn?.id
    const accepted = this.lease.cancelTurn(activeTurnId)
    if (accepted && activeTurnId) {
      this.#optimisticallyCancelledTurnId = activeTurnId
      this.#viewSnapshot = this.readViewSnapshot()
      for (const listener of this.#viewListeners) listener()
    }
    return {
      accepted,
      ...(activeTurnId ? {turnId: activeTurnId} : {}),
    }
  }

  private respondToPrompt(input: InputCompletion): void {
    this.ensureActive()
    this.lease.completeInput({
      requestId: input.requestId,
      response: input.response,
      ...(input.answers ? {answers: input.answers} : {}),
    })
  }

  private confirmTool(confirmation: ToolConfirmationResponse): void {
    this.ensureActive()
    this.lease.confirmToolCall(confirmation)
  }

  private completeTool(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void {
    this.ensureActive()
    this.lease.completeToolCall(input)
  }

  private renameSession(title: string): void {
    this.ensureActive()
    this.lease.renameSession(title)
  }

  private async setApproveAll(enabled: boolean): Promise<void> {
    this.ensureActive()
    await this.ensureMutationLane()
    await this.lease.setApproveAll(enabled)
  }

  private setMode(mode: string): Promise<void> {
    this.ensureActive()
    return this.lease.setMode(mode)
  }
}
